import { Helmet } from "react-helmet-async";

const SITE_URL = "https://ticketin.ec";
const SITE_NAME = "TicketIn";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;
const DEFAULT_DESCRIPTION =
  "TicketIn es la plataforma líder de venta de entradas en Ecuador. Compra boletos para conciertos, deportes, teatro y más de forma segura y rápida.";

interface EventSchema {
  name: string;
  description?: string | null;
  startDate: string | null;
  location: string;
  image?: string | null;
  price?: number;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  /** Structured data para páginas de detalle de evento (JSON-LD Event) */
  eventSchema?: EventSchema;
}

/**
 * SEO-optimized <head> manager using react-helmet-async.
 * Injects per-route: title, description, Open Graph, Twitter Card,
 * canonical URL and optional JSON-LD Event structured data.
 */
export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage,
  ogType = "website",
  noIndex = false,
  eventSchema,
}: SEOHeadProps) {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const resolvedImage = ogImage ?? DEFAULT_OG_IMAGE;
  const resolvedCanonical = canonical ?? (typeof window !== "undefined" ? window.location.href : SITE_URL);

  const eventJsonLd = eventSchema
    ? JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        name: eventSchema.name,
        description: eventSchema.description,
        startDate: eventSchema.startDate,
        location: {
          "@type": "Place",
          name: eventSchema.location,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Cuenca",
            addressCountry: "EC",
          },
        },
        image: eventSchema.image ?? resolvedImage,
        offers: eventSchema.price != null
          ? {
              "@type": "Offer",
              price: eventSchema.price,
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
              url: eventSchema.url,
            }
          : undefined,
        organizer: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
        },
        url: eventSchema.url,
      })
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow" />
      }
      <link rel="canonical" href={resolvedCanonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_EC" />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ticketin_ec" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedImage} />

      {/* JSON-LD Event structured data (rich snippets en Google) */}
      {eventJsonLd && (
        <script type="application/ld+json">{eventJsonLd}</script>
      )}
    </Helmet>
  );
}
